import { motion } from 'framer-motion';
import {
  CreditCard,
  Crown,
  Users,
  CheckCircle,
  AlertTriangle,
  Calendar,
  DollarSign,
  ExternalLink,
  Building,
  ChevronDown,
  ChevronUp,
  Wallet,
  Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import { billingApi, UserBillingInfo, companiesApi, Company, BillingInfo } from '../services/api';
import { analytics } from '../services/analytics';

interface CompanyBillingState {
  company: Company;
  billing: BillingInfo | null;
  isLoading: boolean;
  error: string | null;
  // Pro checkout UI state
  billingCycle: 'monthly' | 'yearly';
  userCount: number;
  isStartingCheckout: boolean;
  showDetails: boolean;
}

export default function BillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkoutResult = searchParams.get('checkout');

  // User-level Founder subscription
  const [userBilling, setUserBilling] = useState<UserBillingInfo | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [userError, setUserError] = useState<string | null>(null);

  // Company-level subscriptions (all companies)
  const [companyStates, setCompanyStates] = useState<CompanyBillingState[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);

  // Founder plan UI
  const [isCancellingFounder, setIsCancellingFounder] = useState(false);
  const [showFounderCancelConfirm, setShowFounderCancelConfirm] = useState(false);

  // Pro activation confirmation modal
  const [pendingProActivation, setPendingProActivation] = useState<CompanyBillingState | null>(
    null
  );

  // Fetch user-level Founder billing
  useEffect(() => {
    const fetchUserBilling = async () => {
      try {
        setIsLoadingUser(true);
        const response = await billingApi.getUserBillingInfo();
        if (response.success && response.data) {
          setUserBilling(response.data);
        } else {
          setUserError(response.error || 'Failed to load billing info');
        }
      } catch {
        setUserError('Failed to load billing info');
      } finally {
        setIsLoadingUser(false);
      }
    };
    fetchUserBilling();
  }, []);

  // Track Pro purchase in GA4/Meta when returning from Stripe checkout (once per session)
  useEffect(() => {
    if (checkoutResult !== 'pro-success' || isLoadingCompanies) return;

    const fired = sessionStorage.getItem('ga_pro_purchase_tracked');
    if (fired) return;

    const proState = companyStates.find(
      (s) => s.billing?.plan === 'pro' && s.billing?.actualAmount != null
    );
    if (!proState?.billing) return;

    sessionStorage.setItem('ga_pro_purchase_tracked', '1');
    const amountDollars = (proState.billing.actualAmount ?? 0) / 100;
    const planLabel = `Pro ${proState.billing.billingCycle} (${proState.billing.proUserCount} users)`;
    analytics.trackSubscription(amountDollars, proState.billing.actualCurrency ?? 'USD', planLabel);
  }, [checkoutResult, isLoadingCompanies, companyStates]);

  // Fetch all companies + their billing info
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setIsLoadingCompanies(true);
        const res = await companiesApi.list();
        if (!res.success || !res.data) return;

        // Only show companies where the user is owner or admin
        const ownedCompanies = res.data.filter((c) => c.role === 'owner' || c.role === 'admin');

        // Init state for each company
        const initial: CompanyBillingState[] = ownedCompanies.map((c) => ({
          company: c,
          billing: null,
          isLoading: true,
          error: null,
          billingCycle: 'monthly',
          userCount: 5,
          isStartingCheckout: false,
          showDetails: true,
        }));
        setCompanyStates(initial);

        // Fetch billing info for each company in parallel
        const billingResults = await Promise.allSettled(
          ownedCompanies.map((c) => billingApi.getBillingInfo(c.id))
        );

        setCompanyStates((prev) =>
          prev.map((state, i) => {
            const result = billingResults[i];
            if (result.status === 'fulfilled' && result.value.success && result.value.data) {
              return { ...state, billing: result.value.data, isLoading: false };
            }
            return {
              ...state,
              isLoading: false,
              error:
                result.status === 'fulfilled'
                  ? result.value.error || 'Failed to load'
                  : 'Failed to load',
            };
          })
        );
      } finally {
        setIsLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, []);

  const updateCompanyState = (companyId: string, patch: Partial<CompanyBillingState>) => {
    setCompanyStates((prev) =>
      prev.map((s) => (s.company.id === companyId ? { ...s, ...patch } : s))
    );
  };

  const handleManageFounderBilling = async () => {
    try {
      const response = await billingApi.createUserBillingPortalSession();
      if (response.success && response.data?.url) {
        window.open(response.data.url, '_blank');
      }
    } catch {
      console.error('Failed to open billing portal');
    }
  };

  const handleManagePaymentMethods = async () => {
    try {
      const response = await billingApi.openPaymentMethodsPortal();
      if (response.success && response.data?.url) {
        window.open(response.data.url, '_blank');
      }
    } catch {
      console.error('Failed to open payment methods portal');
    }
  };

  const handleCancelFounder = async () => {
    try {
      setIsCancellingFounder(true);
      const response = await billingApi.cancelFounderSubscription();
      if (response.success) {
        const refreshRes = await billingApi.getUserBillingInfo();
        if (refreshRes.success && refreshRes.data) setUserBilling(refreshRes.data);
        setShowFounderCancelConfirm(false);
      }
    } catch {
      console.error('Failed to cancel Founder subscription');
    } finally {
      setIsCancellingFounder(false);
    }
  };

  const handleManageProBilling = async (companyId: string) => {
    try {
      const response = await billingApi.createBillingPortalSession(companyId);
      if (response.success && response.data?.url) {
        window.open(response.data.url, '_blank');
      }
    } catch {
      console.error('Failed to open Pro billing portal');
    }
  };

  const handleActivatePro = (state: CompanyBillingState) => {
    setPendingProActivation(state);
  };

  const handleConfirmActivatePro = async () => {
    if (!pendingProActivation) return;
    const state = pendingProActivation;
    setPendingProActivation(null);
    updateCompanyState(state.company.id, { isStartingCheckout: true });
    try {
      const response = await billingApi.createCheckoutSession(state.company.id, {
        userCount: state.userCount,
        billingCycle: state.billingCycle,
      });
      if (response.success && response.data?.url) {
        window.location.href = response.data.url;
      } else {
        updateCompanyState(state.company.id, { isStartingCheckout: false });
      }
    } catch {
      updateCompanyState(state.company.id, { isStartingCheckout: false });
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatAmount = (cents: number | null | undefined) => {
    if (cents == null) return 'N/A';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const tierLabel = (tier: string) => {
    if (tier === 'pro') return 'Pro Activation';
    if (tier === 'founder') return 'Founder';
    return 'Free';
  };

  const tierBadgeClass = (tier: string) => {
    if (tier === 'pro') return 'bg-gradient-to-r from-blue-500 to-purple-600 text-white';
    if (tier === 'founder') return 'bg-gray-800 text-white';
    return 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Subscriptions" showMetrics={false} />

      <main className="max-w-5xl mx-auto px-4 py-8">
        {checkoutResult === 'pro-success' && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-800">Pro Activation successful!</p>
              <p className="text-sm text-green-700">
                Your subscription is now active. It may take a few seconds to reflect below.
              </p>
            </div>
          </div>
        )}
        {checkoutResult === 'canceled' && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Checkout was cancelled.</p>
              <p className="text-sm text-amber-700">
                No charges were made. You can try again whenever you're ready.
              </p>
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Manage Your Subscriptions</h1>
          <p className="text-gray-600">
            View and manage your Founder and Pro Activation subscriptions in one place.
          </p>
        </div>

        {/* ── Founder Plan ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-2.5">
              <Crown className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Founder Plan</h2>
              <p className="text-sm text-gray-500">
                Strategic clarity layer · personal subscription
              </p>
            </div>
          </div>

          {isLoadingUser ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
            </div>
          ) : userError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
              {userError}
            </div>
          ) : userBilling?.isFounder ? (
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Status:</span>
                {userBilling.cancelAtPeriodEnd ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Cancelling
                  </span>
                ) : userBilling.status === 'active' ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-800 text-sm font-medium rounded-full">
                    {userBilling.status || 'Unknown'}
                  </span>
                )}
              </div>

              {/* Pricing */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-600">Amount</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatAmount(userBilling.actualAmount)}
                  </div>
                  {userBilling.discountInfo && (
                    <p className="text-xs text-green-600 mt-1">
                      {userBilling.discountInfo.percentOff
                        ? `${userBilling.discountInfo.percentOff}% off`
                        : userBilling.discountInfo.amountOff
                          ? `${formatAmount(userBilling.discountInfo.amountOff)} off`
                          : 'Discount applied'}
                    </p>
                  )}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-600">
                      {userBilling.cancelAtPeriodEnd ? 'Access Until' : 'Renews On'}
                    </span>
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatDate(userBilling.currentPeriodEnd)}
                  </div>
                </div>
              </div>

              {userBilling.cancelAtPeriodEnd && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">Subscription Cancelling</p>
                    <p className="text-sm text-yellow-800 mt-1">
                      Your Founder plan ends on {formatDate(userBilling.currentPeriodEnd)}. You'll
                      lose access after this date.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleManageFounderBilling}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  <CreditCard className="h-4 w-4" />
                  Manage Billing
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={handleManagePaymentMethods}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                >
                  <Wallet className="h-4 w-4" />
                  Payment Methods
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
                {userBilling.status === 'active' && !userBilling.cancelAtPeriodEnd && (
                  <button
                    onClick={() => setShowFounderCancelConfirm(true)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                  >
                    Cancel Plan
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Crown className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">You don't have an active Founder subscription.</p>
              <button
                onClick={() => navigate('/pricing')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                View Pricing
              </button>
            </div>
          )}
        </motion.div>

        {/* ── Companies ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg p-2.5">
              <Building className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Company Subscriptions</h2>
              <p className="text-sm text-gray-500">
                Pro Activation subscriptions for your companies
              </p>
            </div>
          </div>

          {isLoadingCompanies ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto" />
            </div>
          ) : companyStates.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
              <Building className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">You don't own any companies yet.</p>
              <button
                onClick={() => navigate('/company/setup')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Create a Company
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {companyStates.map((state) => (
                <div
                  key={state.company.id}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  {/* Company header row */}
                  <div className="px-6 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                        {state.company.logo ? (
                          <img
                            src={state.company.logo}
                            alt={state.company.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Building className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{state.company.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{state.company.role}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${tierBadgeClass(state.company.subscriptionTier)}`}
                      >
                        {tierLabel(state.company.subscriptionTier)}
                      </span>
                      <button
                        onClick={() =>
                          updateCompanyState(state.company.id, { showDetails: !state.showDetails })
                        }
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        {state.showDetails ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded details */}
                  {state.showDetails && (
                    <div className="border-t border-gray-100 px-6 py-5">
                      {state.isLoading ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto" />
                        </div>
                      ) : state.error ? (
                        <p className="text-sm text-red-600">{state.error}</p>
                      ) : state.company.subscriptionTier === 'pro' && state.billing ? (
                        /* ── Active Pro plan ── */
                        <div className="space-y-4">
                          <div className="grid sm:grid-cols-3 gap-4">
                            <div className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-center gap-1.5 mb-1">
                                <DollarSign className="h-3.5 w-3.5 text-gray-500" />
                                <span className="text-xs font-medium text-gray-500">Amount</span>
                              </div>
                              <p className="text-lg font-bold text-gray-900">
                                {formatAmount(state.billing.actualAmount)}
                              </p>
                              <p className="text-xs text-gray-500">
                                per {state.billing.billingCycle === 'yearly' ? 'year' : 'month'}
                              </p>
                              {state.billing.discountInfo && (
                                <p className="text-xs text-amber-600 mt-1">
                                  {state.billing.discountInfo.percentOff
                                    ? `${state.billing.discountInfo.percentOff}% launch discount`
                                    : `$${((state.billing.discountInfo.amountOff ?? 0) / 100).toFixed(0)} off`}
                                  {state.billing.discountInfo.duration === 'repeating' &&
                                  state.billing.discountInfo.durationInMonths
                                    ? ` for ${state.billing.discountInfo.durationInMonths} months`
                                    : state.billing.discountInfo.duration === 'once'
                                      ? ' (first payment only)'
                                      : ''}
                                  {' — renews at standard rate'}
                                </p>
                              )}
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-center gap-1.5 mb-1">
                                <Users className="h-3.5 w-3.5 text-gray-500" />
                                <span className="text-xs font-medium text-gray-500">Seats</span>
                              </div>
                              <p className="text-lg font-bold text-gray-900">
                                {state.billing.proUserCount || '—'}
                              </p>
                              <p className="text-xs text-gray-500">active seats</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-center gap-1.5 mb-1">
                                <Calendar className="h-3.5 w-3.5 text-gray-500" />
                                <span className="text-xs font-medium text-gray-500">
                                  {state.billing.cancellingOn ? 'Access Until' : 'Renews'}
                                </span>
                              </div>
                              <p className="text-sm font-semibold text-gray-900">
                                {formatDate(
                                  state.billing.cancellingOn || state.billing.currentPeriodEnd
                                )}
                              </p>
                            </div>
                          </div>

                          {state.billing.cancellingOn && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex gap-2">
                              <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-yellow-800">
                                Pro Activation ends on {formatDate(state.billing.cancellingOn)}.
                              </p>
                            </div>
                          )}

                          <div className="flex gap-3 pt-2">
                            <button
                              onClick={() => handleManageProBilling(state.company.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
                            >
                              <CreditCard className="h-4 w-4" />
                              Manage Billing
                              <ExternalLink className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* ── Upgrade to Pro ── */
                        <div>
                          <p className="text-sm text-gray-600 mb-4">
                            Activate Pro to unlock AI strategy generation, task delegation, KPI
                            tracking, and the full execution engine for this company.
                          </p>

                          <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-5">
                            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
                              <Crown className="h-4 w-4 text-blue-600" />
                              Pro Activation
                            </h3>

                            {/* Billing cycle */}
                            <div className="flex items-center gap-2 mb-4">
                              <button
                                onClick={() =>
                                  updateCompanyState(state.company.id, { billingCycle: 'monthly' })
                                }
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                  state.billingCycle === 'monthly'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                                }`}
                              >
                                Monthly
                              </button>
                              <button
                                onClick={() =>
                                  updateCompanyState(state.company.id, { billingCycle: 'yearly' })
                                }
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                  state.billingCycle === 'yearly'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                                }`}
                              >
                                Yearly
                                <span className="ml-1.5 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
                                  Save 17%
                                </span>
                              </button>
                            </div>

                            {/* User count */}
                            <div className="mb-4">
                              <label className="text-sm font-medium text-gray-700 block mb-1.5">
                                Number of Users{' '}
                                <span className="text-xs text-gray-500">(min 5)</span>
                              </label>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() =>
                                    updateCompanyState(state.company.id, {
                                      userCount: Math.max(5, state.userCount - 1),
                                    })
                                  }
                                  className="w-8 h-8 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 text-gray-700 font-bold"
                                >
                                  −
                                </button>
                                <span className="w-10 text-center font-semibold text-gray-900">
                                  {state.userCount}
                                </span>
                                <button
                                  onClick={() =>
                                    updateCompanyState(state.company.id, {
                                      userCount: state.userCount + 1,
                                    })
                                  }
                                  className="w-8 h-8 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 text-gray-700 font-bold"
                                >
                                  +
                                </button>
                              </div>
                            </div>

                            {/* Price summary */}
                            <div className="bg-white/70 rounded-lg px-4 py-3 mb-4 border border-blue-100 space-y-2">
                              {/* Standard → Launch per user */}
                              <div className="flex items-baseline justify-between">
                                <span className="text-sm text-gray-500">
                                  {state.userCount} users ×{' '}
                                  {state.billingCycle === 'yearly' ? 'year' : 'month'}
                                </span>
                                <span className="flex items-baseline gap-2">
                                  <span className="text-sm text-gray-400 line-through">
                                    $
                                    {(
                                      state.userCount *
                                      (state.billingCycle === 'yearly' ? 1000 : 100)
                                    ).toLocaleString()}
                                  </span>
                                  <span className="text-lg font-bold text-gray-900">
                                    $
                                    {(
                                      state.userCount * (state.billingCycle === 'yearly' ? 240 : 20)
                                    ).toLocaleString()}
                                    <span className="text-sm font-normal text-gray-500">
                                      /{state.billingCycle === 'yearly' ? 'yr' : 'mo'}
                                    </span>
                                  </span>
                                </span>
                              </div>
                              <p className="text-xs text-blue-700 font-medium">
                                Launch pricing · Year 1 only · renews at $
                                {state.billingCycle === 'yearly' ? '1,000' : '100'}/user
                              </p>
                            </div>

                            <button
                              onClick={() => handleActivatePro(state)}
                              disabled={state.isStartingCheckout}
                              className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                              {state.isStartingCheckout ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                  Activating…
                                </>
                              ) : (
                                <>
                                  <Zap className="h-4 w-4" />
                                  Activate Pro Activation
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      {/* Cancel Founder Confirmation Modal */}
      {showFounderCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Cancel Founder Subscription?
            </h3>
            <p className="text-gray-600 mb-6">
              Your subscription will remain active until {formatDate(userBilling?.currentPeriodEnd)}
              . After that, you'll lose access to the dashboard and all Founder features.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowFounderCancelConfirm(false)}
                disabled={isCancellingFounder}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelFounder}
                disabled={isCancellingFounder}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
              >
                {isCancellingFounder ? 'Cancelling…' : 'Cancel Subscription'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
      {/* Pro Activation Confirmation Modal */}
      {pendingProActivation &&
        (() => {
          const s = pendingProActivation;
          const isYearly = s.billingCycle === 'yearly';
          const launchPrice = isYearly ? 240 : 20;
          const standardPrice = isYearly ? 1000 : 100;
          const launchTotal = s.userCount * launchPrice;
          const standardTotal = s.userCount * standardPrice;
          const period = isYearly ? 'yr' : 'mo';
          return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Zap className="h-5 w-5 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Confirm Pro Activation</h3>
                </div>

                <p className="text-gray-600 mb-5 text-sm">
                  You're about to activate Pro for{' '}
                  <span className="font-semibold text-gray-900">{s.company.name}</span>.
                </p>

                <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200 mb-5">
                  <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-sm text-gray-600">Billing cycle</span>
                    <span className="text-sm font-medium text-gray-900 capitalize">
                      {s.billingCycle}
                    </span>
                  </div>
                  <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-sm text-gray-600">Users</span>
                    <span className="text-sm font-medium text-gray-900">{s.userCount}</span>
                  </div>
                  <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-sm text-gray-600">You pay (Year 1)</span>
                    <span className="flex items-baseline gap-2">
                      <span className="text-sm text-gray-400 line-through">
                        ${standardTotal.toLocaleString()}/{period}
                      </span>
                      <span className="text-sm font-semibold text-green-700">
                        ${launchTotal.toLocaleString()}/{period}
                      </span>
                    </span>
                  </div>
                  <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-sm text-gray-600">Renews at (Year 2+)</span>
                    <span className="text-sm text-gray-500">
                      ${standardTotal.toLocaleString()}/{period}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-5">
                  Launch pricing is valid for the first year only. Renews at standard rates.
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => setPendingProActivation(null)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmActivatePro}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity font-semibold text-sm"
                  >
                    Confirm & Activate
                  </button>
                </div>
              </motion.div>
            </div>
          );
        })()}
    </div>
  );
}
